import axios from 'axios';
import map from 'lodash/map';
import reduce from 'lodash/reduce';
import { parseKeysSnakeCaseToCamelCase } from 'utils/textFormats';

function buildUrl(rootDomain, eventId, action) {
    return `https://${rootDomain}/events/confirmations/${eventId}/${action}`;
}

function addLocationToEachConfirmation(confirmations) {
    return map(confirmations, (typedConfirmations, location) =>
        // Add all confirmation model data to a single array
        map(typedConfirmations, (confirmationDescriptor) => ({
            ...confirmationDescriptor,
            location,
        })),
    );
}

function flattenConfirmations(confirmations) {
    return reduce(confirmations, (memo, typedConfirmations) => memo.concat.call(memo, typedConfirmations), []);
}

/**
 * Adds event data needed on the confirmation
 * @param { [{}] } confirmations - Expects the flattened confirmations
 * @return { [{}] }
 */
function addEventDataToEachConfirmation(confirmations) {
    const isHubEvent = window.splash.server._event.hub;

    return map(confirmations, (confirmation) => ({
        ...confirmation,
        isHubEvent,
    }));
}

function formatForBackboneModels(confirmations) {
    /**
     * @type {string[]} keys for which the response initial values should be parsed as *_fallback
     */
    const keysToSetAsFallback = ['subject', 'headline', 'messaging', 'content', 'pdfContent'];

    return confirmations.map((confirmation) => {
        const initials = {};

        // eg: transform `subject` to `subject_fallback` but keep `active` intact.
        Object.keys(confirmation.initial).forEach((key) => {
            const newKey = keysToSetAsFallback.indexOf(key) !== -1 ? `${key}_fallback` : key;
            initials[newKey] = confirmation.initial[key];
        });

        const result = {
            ...confirmation,
            ...initials,
            ...confirmation.custom,
        };

        delete result.initial;
        delete result.custom;

        return result;
    });
}

function setTicketNamesInCollectionData(collectionData, tickets) {
    // If there aren't any tickets, return the data as is
    if (typeof tickets === 'undefined') {
        return collectionData;
    }

    const ticketsById = {};
    tickets.forEach((ticket) => {
        ticketsById[ticket.id] = ticket;
    });

    // Set ticket names on the ticket-custom triggers.
    const mappedCollectionData = collectionData.map((triggerData) => {
        const triggerCopy = { ...triggerData };
        if (triggerCopy.ticket_type_id) {
            const ticket = ticketsById[triggerCopy.ticket_type_id];
            if (ticket) {
                triggerCopy.ticketName = ticket.name;
            }
        }
        return triggerCopy;
    });

    return mappedCollectionData;
}

function setEventMessageDataInCollectionData(collectionData, eventMessages) {
    if (typeof eventMessages === 'undefined') {
        return collectionData;
    }

    eventMessages.forEach((em) => {
        const triggerData = collectionData.find(
            (triggerDatum) => triggerDatum.event_message_id === parseInt(em.id, 10),
        );

        if (triggerData) {
            // eslint-disable-next-line no-param-reassign
            delete em.id;
            triggerData.eventMessageData = parseKeysSnakeCaseToCamelCase(em);
        }
    });

    return collectionData;
}

function parseBootstrapData(response) {
    const data = {};
    const confirmationTypes = {
        page: response.confirmations.page,
        email: response.confirmations.email,
        sms: response.confirmations.sms,
    };
    let confs = addLocationToEachConfirmation(confirmationTypes);

    confs = flattenConfirmations(confs);
    confs = addEventDataToEachConfirmation(confs);

    data.confirmations = formatForBackboneModels(confs);

    data.tickets = Array.isArray(response.tickets) ? response.tickets : [];
    data.eventMessages = Array.isArray(response.event_messages) ? response.event_messages : [];

    data.confirmations = setTicketNamesInCollectionData(data.confirmations, data.tickets);
    data.onPageConfirmationsVersion = response.onPageConfirmationsVersion;

    if (data.eventMessages) {
        data.confirmations = setEventMessageDataInCollectionData(data.confirmations, data.eventMessages);
    }

    return data;
}

export function fetchBootstrapData(rootDomain, eventId) {
    const requestUrl = buildUrl(rootDomain, eventId, 'getBootstrapData');

    return axios.get(requestUrl, { withCredentials: true }).then((res) => parseBootstrapData(res.data.data));
}

export function deleteOnPageConfirmationElement(eventId, triggerType) {
    const requestUrl = `/events/confirmations/deleteOnPageConfirmationElement/${eventId}/${triggerType}`; // eslint-disable-line max-len

    return axios.delete(requestUrl);
}

export function upgradeToV2(eventId, csrfToken) {
    const requestUrl = `/events/confirmations/${eventId}/upgradeToV2?csrfToken=${csrfToken}`; // eslint-disable-line max-len

    return axios.post(requestUrl);
}

export function fetchConfirmationConfigData(rootDomain, eventId, triggerType) {
    const requestUrl = `https://${rootDomain}/events/confirmations/${eventId}/dynamic-tags/${triggerType}`;

    return axios.get(requestUrl, { withCredentials: true }).then((res) => res.data.data);
}
